<script setup lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
import FacetCard from "./FacetCard.vue";
import ColumnCard from "./ColumnCard.vue";
import FieldPicker from "./FieldPicker.vue";
import {
  discoverFields,
  normalizeConfig,
  defaultDisplayType,
  buildFieldIndex,
  type Field,
  type FacetConfig,
  type ColumnConfig,
  type DisplayType,
} from "../../../content/reports/ranked-explorer/fields";

type SortOrder = "asc" | "desc";

const props = defineProps<{ id: string }>();

const { block, patch } = useInlineBlockEditor(props.id);
const { contentState, findParentOf, getContentById } = useContentManager();

// --- Report data for field discovery ---
// This editor renders in the resource-editor panel — a separate subtree from the
// report preview — so it can't inject the provider's `report`. Resolve the
// nearest report-provider ancestor and read the raw users it publishes to global
// state (keyed by the provider's block id). Field discovery therefore runs
// against the real report data, independent of the provider's custom mapper
// (which may reshape `report.data` and hide constructs/qualifiers).
const PROVIDER_IDS = new Set<string>([
  ComponentDefinitionIds.REPORT_PROVIDER_RAW,
  ComponentDefinitionIds.REPORT_PROVIDER,
]);

function resolveProviderId(): string | null {
  const items = contentState.value ?? [];
  let current = findParentOf(props.id, items);
  while (current) {
    if (PROVIDER_IDS.has(current.componentId)) return current.id;
    current = findParentOf(current.id, items);
  }
  return null;
}

const providerId = resolveProviderId();

const fieldSourceUsers = useState<any[] | null>(
  providerId ? `report-fieldsource:${providerId}` : "report-fieldsource:__none__",
  () => null,
);
const { reportData, fetchReports } = useReportsStore(providerId ?? undefined);

const rawUsers = ref<any[]>([]);

/** The report file configured on the resolved provider ancestor. */
function providerReportFileName(): string | undefined {
  if (!providerId) return undefined;
  const raw = getContentById(providerId)?.reportFileName;
  return typeof raw === "string" && raw.trim().length ? raw.trim() : "report";
}

function syncData() {
  const published = fieldSourceUsers.value;
  if (Array.isArray(published)) {
    rawUsers.value = published;
    return;
  }
  rawUsers.value = Array.isArray(reportData.value) ? reportData.value : [];
}

onMounted(async () => {
  // Ensure field discovery has data to work with. Prefer whatever the provider has
  // already published (fieldSourceUsers) or loaded into its store (reportData);
  // otherwise load the provider's configured report file ourselves. This keeps the
  // picker populated regardless of provider type (the non-raw provider publishes no
  // field source) or whether the provider has finished its own load yet. The report
  // API always serves the system-cache aggregate, so this reflects the live data.
  const hasData =
    (Array.isArray(fieldSourceUsers.value) && fieldSourceUsers.value.length > 0) ||
    (Array.isArray(reportData.value) && reportData.value.length > 0);
  if (!hasData) {
    const fileName = providerReportFileName();
    await fetchReports(undefined, fileName ? { fileName } : undefined);
  }
  syncData();
});

watch(fieldSourceUsers, syncData, { deep: true });
watch(reportData, syncData, { deep: true });

const fieldCatalog = computed<Field[]>(() => discoverFields(rawUsers.value));
const catalogById = computed(() => buildFieldIndex(fieldCatalog.value));

// --- Normalized config ---
const config = computed(() => normalizeConfig(block.value));

// --- Field picker state ---
const pickerOpen = ref(false);
const pickerMode = ref<"filter" | "column" | "any">("any");

const usedFilters = computed(() => new Set(config.value.facets.map((f: FacetConfig) => f.fieldId)));
const usedColumns = computed(() => new Set(config.value.columns.map((c: ColumnConfig) => c.fieldId)));

function openPicker(mode: "filter" | "column" | "any") {
  pickerMode.value = mode;
  pickerOpen.value = true;
}

// --- Mutations ---
function addField(field: Field, role: "filter" | "column") {
  if (role === "filter") {
    if (usedFilters.value.has(field.id)) return;
    const facets: FacetConfig[] = [
      ...config.value.facets,
      {
        fieldId: field.id,
        label: field.label,
        mode: field.kind === "options" ? "options" : "text",
        defaultOpen: true,
        allowNone: true,
      },
    ];
    patch({ facets });
  } else {
    if (usedColumns.value.has(field.id)) return;
    const columns: ColumnConfig[] = [
      ...config.value.columns,
      {
        fieldId: field.id,
        label: field.label,
        display: defaultDisplayType(field),
        align: field.kind === "number" ? "right" : "left",
      },
    ];
    patch({ columns });
  }
}

function updateFacet(fieldId: string, facetPatch: Partial<FacetConfig>) {
  const facets = config.value.facets.map((f: FacetConfig) =>
    f.fieldId === fieldId ? { ...f, ...facetPatch } : f
  );
  patch({ facets });
}

function removeFacet(fieldId: string) {
  patch({ facets: config.value.facets.filter((f: FacetConfig) => f.fieldId !== fieldId) });
}

function moveFacet(from: number, to: number) {
  const facets = [...config.value.facets];
  const [moved] = facets.splice(from, 1);
  facets.splice(to, 0, moved);
  patch({ facets });
}

function updateColumn(fieldId: string, colPatch: Partial<ColumnConfig>) {
  const columns = config.value.columns.map((c: ColumnConfig) =>
    c.fieldId === fieldId ? { ...c, ...colPatch } : c
  );
  patch({ columns });
}

function removeColumn(fieldId: string) {
  const columns = config.value.columns.filter((c: ColumnConfig) => c.fieldId !== fieldId);
  const general = { ...config.value.general };
  if (general.sortFieldId === fieldId) general.sortFieldId = "";
  patch({ columns, general });
}

function moveColumn(from: number, to: number) {
  const columns = [...config.value.columns];
  const [moved] = columns.splice(from, 1);
  columns.splice(to, 0, moved);
  patch({ columns });
}

function updateGeneral(generalPatch: Record<string, any>) {
  patch({ general: { ...config.value.general, ...generalPatch } });
}

// --- Sort column options ---
const sortColumnOptions = computed(() =>
  config.value.columns.map((c: ColumnConfig) => ({
    value: c.fieldId,
    label: c.label || catalogById.value.get(c.fieldId)?.label || c.fieldId,
  }))
);

// --- Drag and drop ---
const dragIdx = ref<number | null>(null);
const dropIdx = ref<number | null>(null);
const dragContext = ref<"facets" | "columns" | null>(null);

function onDragStart(ctx: "facets" | "columns", index: number) {
  dragContext.value = ctx;
  dragIdx.value = index;
}

function onDragOver(ctx: "facets" | "columns", index: number, e: DragEvent) {
  if (dragContext.value !== ctx) return;
  e.preventDefault();
  dropIdx.value = index;
}

function onDrop(ctx: "facets" | "columns") {
  if (dragContext.value !== ctx || dragIdx.value === null || dropIdx.value === null) return;
  if (dragIdx.value !== dropIdx.value) {
    if (ctx === "facets") moveFacet(dragIdx.value, dropIdx.value);
    else moveColumn(dragIdx.value, dropIdx.value);
  }
  dragIdx.value = null;
  dropIdx.value = null;
  dragContext.value = null;
}

function onDragEnd() {
  dragIdx.value = null;
  dropIdx.value = null;
  dragContext.value = null;
}
</script>

<template>
  <!-- Facet Filters -->
  <EditorSection title="Facet Filters" icon="lucide:filter" accent="reporting">
    <div class="flex items-center justify-between gap-2 -mt-1 mb-1">
      <p class="text-[11px] text-muted-foreground">
        {{ config.facets.length }} facet{{ config.facets.length !== 1 ? "s" : "" }} on the report sidebar
      </p>
      <Button variant="outline" size="sm" class="h-7 rounded-sm text-xs" @click="openPicker('filter')">
        <Icon mode="svg" size="14" name="lucide:plus" class="mr-1" />
        Add
      </Button>
    </div>

    <div v-if="config.facets.length" class="space-y-2">
      <div
        v-for="(facet, index) in config.facets"
        :key="facet.fieldId"
        draggable="true"
        class="relative"
        @dragstart="onDragStart('facets', index)"
        @dragover="(e: DragEvent) => onDragOver('facets', index, e)"
        @drop="onDrop('facets')"
        @dragend="onDragEnd"
      >
        <div
          v-if="dropIdx === index && dragContext === 'facets' && dragIdx !== index"
          class="absolute -top-1.5 left-0 right-0 h-0.5 bg-primary rounded z-10"
        />
        <FacetCard
          :facet="facet"
          :field="catalogById.get(facet.fieldId)"
          :class="dragIdx === index && dragContext === 'facets' ? 'opacity-40' : ''"
          @update="(p: Partial<FacetConfig>) => updateFacet(facet.fieldId, p)"
          @remove="removeFacet(facet.fieldId)"
        />
      </div>
    </div>
    <div v-else class="border border-dashed rounded-lg py-5 px-4 text-center text-muted-foreground">
      <Icon mode="svg" size="20" name="lucide:filter" class="mx-auto opacity-55" />
      <p class="text-xs mt-1.5 leading-snug">
        Filters become the facets down the left of the report.
      </p>
    </div>
  </EditorSection>

  <!-- Display Columns -->
  <EditorSection title="Display Columns" icon="lucide:columns-3" accent="reporting">
    <div class="flex items-center justify-between gap-2 -mt-1 mb-1">
      <p class="text-[11px] text-muted-foreground">
        {{ config.columns.length }} column{{ config.columns.length !== 1 ? "s" : "" }} in the table
      </p>
      <Button variant="outline" size="sm" class="h-7 rounded-sm text-xs" @click="openPicker('column')">
        <Icon mode="svg" size="14" name="lucide:plus" class="mr-1" />
        Add
      </Button>
    </div>

    <div v-if="config.columns.length" class="space-y-2">
      <div
        v-for="(col, index) in config.columns"
        :key="col.fieldId"
        draggable="true"
        class="relative"
        @dragstart="onDragStart('columns', index)"
        @dragover="(e: DragEvent) => onDragOver('columns', index, e)"
        @drop="onDrop('columns')"
        @dragend="onDragEnd"
      >
        <div
          v-if="dropIdx === index && dragContext === 'columns' && dragIdx !== index"
          class="absolute -top-1.5 left-0 right-0 h-0.5 bg-primary rounded z-10"
        />
        <ColumnCard
          :col="col"
          :field="catalogById.get(col.fieldId)"
          :class="dragIdx === index && dragContext === 'columns' ? 'opacity-40' : ''"
          @update="(p: Partial<ColumnConfig>) => updateColumn(col.fieldId, p)"
          @remove="removeColumn(col.fieldId)"
        />
      </div>
    </div>
    <div v-else class="border border-dashed rounded-lg py-5 px-4 text-center text-muted-foreground">
      <Icon mode="svg" size="20" name="lucide:columns-3" class="mx-auto opacity-55" />
      <p class="text-xs mt-1.5 leading-snug">
        Columns make up the table, with their own display type.
      </p>
    </div>
  </EditorSection>

  <!-- General -->
  <EditorSection title="General" icon="lucide:settings" accent="reporting">
    <EditorField label="Individual Report Path" hint="Where a row click navigates.">
      <EditorPathInput
        :model-value="config.general.individualReportPath"
        mode="existing-file"
        :scopes="['site']"
        content-type-filter="report"
        placeholder="/reports/individual"
        aria-label="Individual report path"
        @update:model-value="(v: string) => updateGeneral({ individualReportPath: v })"
      />
    </EditorField>

    <div class="grid grid-cols-2 gap-3">
      <EditorField label="Page Size">
        <Input
          :model-value="config.general.pageSize"
          type="number"
          min="1"
          class="h-8 rounded-sm bg-muted text-[13px]"
          @update:model-value="(v: string) => updateGeneral({ pageSize: Math.max(1, parseInt(v) || 25) })"
        />
      </EditorField>

      <EditorField label="Sort Order">
        <Select
          :model-value="config.general.sortOrder"
          @update:model-value="(v: string) => updateGeneral({ sortOrder: v })"
        >
          <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="desc">High → Low</SelectItem>
            <SelectItem value="asc">Low → High</SelectItem>
          </SelectContent>
        </Select>
      </EditorField>
    </div>

    <EditorField label="Default Sort Column" hint="Pick from your display columns.">
      <Select
        :model-value="config.general.sortFieldId || '__none__'"
        @update:model-value="(v: string) => updateGeneral({ sortFieldId: v === '__none__' ? '' : v })"
      >
        <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">None</SelectItem>
          <SelectItem v-for="opt in sortColumnOptions" :key="opt.value" :value="opt.value">
            {{ opt.label }}
          </SelectItem>
        </SelectContent>
      </Select>
    </EditorField>
  </EditorSection>

  <!-- Field picker dialog -->
  <FieldPicker
    v-model:open="pickerOpen"
    :mode="pickerMode"
    :fields="fieldCatalog"
    :used-filters="usedFilters"
    :used-columns="usedColumns"
    @pick="addField"
  />
</template>
